import { Vector3 } from '../../types/Vector3'
import { Quaternion } from '../../types/Quaternion'
import { LayoutInterface, AlignmentMode } from '../../field/FieldTypes'
import { FieldParameters } from '../../field/FieldParameters'
import { CurveUtils } from '../../../math/CurveUtils'
import { VectorUtils } from '../../../math/VectorUtils'
import { IndexTransforms } from '../../../math/IndexTransforms'

export class HexSphericalLayout implements LayoutInterface {
  // ==================== POSITION CALCULATION ====================
  
  /**
   * Calculate position for object at given index on sphere surface
   */
  public computePosition(index: number, totalCount: number, params: FieldParameters): Vector3 {
    // Convert index to hex coordinates
    const coords = IndexTransforms.indexToHex(index)
    
    // Calculate sphere radius
    const radius = this.calculateSphereRadius(params, totalCount)
    
    // Get position on sphere using hex distribution
    const position = CurveUtils.getPointOnHexSphere(
      new Vector3(), // Center at origin
      radius,
      coords.ring,
      coords.positionInRing
    )
    
    return position
  }
  
  // ==================== ORIENTATION CALCULATION ====================
  
  /**
   * Calculate orientation for object at given index on sphere surface using 2D surface mathematics
   */
  public computeOrientation(index: number, totalCount: number, params: FieldParameters): Quaternion {
    const coords = IndexTransforms.indexToHex(index)
    const position = this.computePosition(index, totalCount, params)
    const sphereCenter = new Vector3() // Sphere centered at origin
    
    // Convert hex coordinates to spherical parameters
    // For spherical hex distribution, we need to map to longitude/latitude
    const maxRing = Math.max(1, Math.floor(Math.sqrt(totalCount / 6))) // Estimate max ring
    const phi = coords.positionInRing / (6 * coords.ring || 1) // Longitude parameter (0-1)
    const theta = coords.ring / maxRing // Latitude parameter (0-1)
    
    // Use the improved 2D surface orientation calculation with existing enums
    const { right, up, forward } = CurveUtils.calculate2DSurfaceOrientation(
      'sphere', // Spherical surface
      position,
      null, // No plane normal for spheres
      sphereCenter, // Sphere center
      phi, // U coordinate (longitude, 0-1)
      theta, // V coordinate (latitude, 0-1) 
      params.alignmentMode, // Already AlignmentMode enum
      params.upVectorMode // Already UpVectorMode enum
    )
    
    // Convert to quaternion using lookDir
    return VectorUtils.lookRotation(forward, up)
  }
  
  // ==================== TANGENT CALCULATION ====================
  
  /**
   * Calculate tangent vector (direction along sphere surface)
   */
  public computeTangent(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const coords = IndexTransforms.indexToHex(index)
    const position = this.computePosition(index, totalCount, params)
    const center = new Vector3()
    
    return CurveUtils.getSphereTangent(center, position, coords.ring, coords.positionInRing)
  }
  
  // ==================== NORMAL CALCULATION ====================
  
  /**
   * Calculate normal vector (radial direction from sphere center)
   */
  public computeNormal(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const position = this.computePosition(index, totalCount, params)
    const center = new Vector3()
    
    return CurveUtils.getSphereNormal(center, position)
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Calculate sphere radius based on parameters
   */
  private calculateSphereRadius(params: FieldParameters, totalCount: number): number {
    if (params.radius && params.radius > 0) {
      return params.radius
    }
    
    // Auto-calculate radius based on total count and desired spacing
    const spacing = params.cellSize + params.cellSpacing
    const surfaceArea = totalCount * spacing * spacing
    const radius = Math.sqrt(surfaceArea / (4 * Math.PI))
    
    return Math.max(radius, 5) // Minimum radius of 5
  }
  
  /**
   * Get main directional vector of the layout (sphere center to surface)
   */
  public getDirectionalVector(params: FieldParameters): Vector3 {
    // For sphere, the directional vector varies per position
    // Return a default upward direction
    return new Vector3(0, 1, 0)
  }
  
  /**
   * Get sphere bounds
   */
  public getSphereBounds(params: FieldParameters, totalCount: number): { 
    center: Vector3
    radius: number
  } {
    const radius = this.calculateSphereRadius(params, totalCount)
    
    return {
      center: new Vector3(0, 0, 0),
      radius: radius
    }
  }
  
  /**
   * Get maximum ring number for given total count
   */
  public getMaxRing(totalCount: number): number {
    if (totalCount <= 1) return 0
    
    let ring = 1
    let totalPoints = 1 // Center point
    
    while (totalPoints < totalCount) {
      totalPoints += ring * 6
      ring++
    }
    
    return ring - 1
  }
  
  /**
   * Get all indices in a specific ring
   */
  public getRingIndices(ring: number): number[] {
    if (ring === 0) return [0]
    
    const indices: number[] = []
    const startIndex = IndexTransforms.getTotalHexPoints(ring - 1)
    const pointsInRing = ring * 6
    
    for (let i = 0; i < pointsInRing; i++) {
      indices.push(startIndex + i)
    }
    
    return indices
  }
  
  /**
   * Check if index is at north or south pole
   */
  public isAtPole(index: number, totalCount: number): boolean {
    const coords = IndexTransforms.indexToHex(index)
    const maxRing = this.getMaxRing(totalCount)
    
    return coords.ring === 0 || coords.ring === maxRing
  }
  
  /**
   * Get spherical coordinates (latitude, longitude) for index
   */
  public getSphericalCoordinates(index: number, totalCount: number): {
    latitude: number
    longitude: number
  } {
    const coords = IndexTransforms.indexToHex(index)
    const maxRing = this.getMaxRing(totalCount)
    
    if (coords.ring === 0) {
      return { latitude: Math.PI / 2, longitude: 0 } // North pole
    }
    
    const latitude = Math.PI * (0.5 - coords.ring / maxRing)
    const pointsInRing = coords.ring * 6
    const longitude = (coords.positionInRing / pointsInRing) * Math.PI * 2
    
    return { latitude, longitude }
  }
  
  /**
   * Get distance between two points on sphere surface
   */
  public getSphericalDistance(
    index1: number, 
    index2: number, 
    totalCount: number, 
    params: FieldParameters
  ): number {
    const pos1 = this.computePosition(index1, totalCount, params)
    const pos2 = this.computePosition(index2, totalCount, params)
    const radius = this.calculateSphereRadius(params, totalCount)
    
    // Calculate angular distance
    const dot = pos1.dot(pos2) / (radius * radius)
    const angle = Math.acos(Math.max(-1, Math.min(1, dot)))
    
    return angle * radius
  }
  
  /**
   * Get optimal sphere size for given parameters
   */
  public getOptimalSphereRadius(targetDensity: number, totalCount: number): number {
    const surfaceArea = totalCount / targetDensity
    return Math.sqrt(surfaceArea / (4 * Math.PI))
  }
} 